import { faArrowAltFromLeft, faFolder, faStar } from '@fortawesome/pro-light-svg-icons';
import {
    faArrowAltFromLeft as solidFaArrowAltFromLeft,
    faFolder as solidFaFolder,
    faStar as solidFaStar,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ContentType,
    GroupFileAssignmentRequest,
    GroupFilesAssignmentMode,
    GroupFilesAssignmentRequest,
    GroupPictureRole,
    IGroupPictureFileMetadataResponse,
    IPictureFileMetadataResponse,
    PictureFileMetadataResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { FileLibraryGroup } from 'components/asset/asset_file_library/types';
import { DeleteIcon } from 'components/icon/DeleteIcon';
import { FullScreenIcon } from 'components/icon/FullScreenIcon';
import { RemoveIcon } from 'components/icon/RemoveIcon';
import { ActionMenu } from 'components/menu/ActionMenu';
import { ActionMenuButton } from 'components/menu/ActionMenuButton';
import { ActionMenuItem } from 'components/menu/ActionMenuItem';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { usePopup } from 'custom_hooks/usePopup';
import { FolderModal } from 'pages/Settings/FileLibrary/Folder/FolderModal';
import { DirentMoveModal } from 'pages/Settings/FileLibrary/Folder/MoveFolderModal';
import { getDeleteFileModalText } from 'pages/Settings/FileLibrary/shared';
import { useMemo, useState } from 'react';
import { useThemeValues } from 'styles/theme';

type FileIsh = IPictureFileMetadataResponse | IGroupPictureFileMetadataResponse;

export type FileLibraryActionsCellProps = Readonly<{
    group?: FileLibraryGroup;
    enableModal: boolean;
    rowData: FileIsh;
    onChange: () => void;
    locationId?: string;
    corporationId?: string;
    /**
     * if being rendered in a "group images table", that group's id
     */
    contextGroupId?: string;
    foldersHierarchy: IPictureFileMetadataResponse[];
}>;

const getContentDeleteToastMessage = (fileType: ContentType, success: boolean): string =>
    success
        ? `Successfully deleted ${fileType.toLowerCase()}`
        : `Failed to delete ${fileType.toLowerCase()}`;

function isPrimaryImage(file: FileIsh) {
    if ('IsPrimaryImage' in file) {
        return file.IsPrimaryImage;
    }
    if ('Role' in file) {
        return file.Role === GroupPictureRole.Overview;
    }
}

export function FileLibraryActionsCell({
    group,
    enableModal,
    rowData,
    onChange,
    locationId,
    corporationId,
    contextGroupId,
    foldersHierarchy,
}: FileLibraryActionsCellProps) {
    const { secondaryText } = useThemeValues();
    type Action = 'delete' | 'unlink-group' | 'edit-folder' | 'move';
    const [activeAction, setActiveAction] = useState<Action>();
    const { canEditFileLibrary, canDeleteFromFileLibrary } = usePermissions();
    const { showToast } = useNotificationContext();

    const fileDeleteText = useMemo(
        () =>
            getDeleteFileModalText({
                ...rowData,
                FileType: 'FileType' in rowData ? rowData.FileType : ContentType.Picture,
            }),
        [rowData]
    );

    const {
        isOpen: isActionMenuOpen,
        toggleOpen: toggleActionMenuOpen,
        close: closeActionMenu,
        popupRef,
        triggerRef,
    } = usePopup();

    const { mutate: onOpenFullSizeImage } = useMutation({
        mutationFn: async (picture: FileIsh) => {
            if (picture.FileName) {
                const file = await sdClient.getFileObject({
                    objectFilename: picture.FileName,
                });
                return file.data;
            }
            return null;
        },

        onSuccess: (data) => {
            closeActionMenu();
            if (data) {
                const blobUrl = URL.createObjectURL(data);
                window.open(blobUrl);
            }
        },

        onError: () => {
            showToast({
                type: 'error',
                content: `Unable to load file.`,
            });
        },
    });

    const fileType = 'FileType' in rowData ? rowData.FileType : ContentType.Picture;
    const { mutate: onDeleteConfirm } = useMutation({
        mutationFn: () => sdClient.deleteFile({ fileId: rowData.Id }),

        onSuccess: () => {
            onChange();
            setActiveAction(undefined);
            showToast({
                type: 'success',
                content: getContentDeleteToastMessage(fileType, true),
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: getContentDeleteToastMessage(fileType, false),
            }),
    });

    const { mutate: onRemoveAssociationConfirm } = useMutation({
        mutationFn: async () => {
            if (!(rowData.FileName && group)) {
                return;
            }

            await sdClient.assignFilesToGroups({
                body: new GroupFilesAssignmentRequest({
                    Mode: GroupFilesAssignmentMode.Remove,
                    Assignments: [
                        new GroupFileAssignmentRequest({
                            GroupId: group.Id,
                            PictureId: rowData.Id,
                            // role has no effect here, since removing
                            Role: GroupPictureRole.Other,
                        }),
                    ],
                }),
            });
        },

        onSuccess: () => {
            onChange();
            setActiveAction(undefined);
            closeActionMenu();
        },

        onError: () => {
            showToast({
                type: 'error',
                content: 'Unable to remove file association.',
            });
            setActiveAction(undefined);
        },
    });

    const { mutate: makePrimaryImage } = useMutation({
        mutationFn: async (
            picture: Pick<IPictureFileMetadataResponse, 'FileName' | 'Id'>
        ) => {
            if (picture.FileName && group) {
                await sdClient.setPrimaryImageForGroup({
                    groupId: group.Id,
                    body: picture.Id,
                });
            }
        },

        onSuccess: () => {
            onChange();
            closeActionMenu();
        },

        onError: () => {
            showToast({
                type: 'error',
                content: `Unable to set primary image.`,
            });
        },
    });

    const hasGroupActions = group && (!contextGroupId || group.Id === contextGroupId);

    const isPrimary = isPrimaryImage(rowData);

    return (
        <ActionMenuTableCell>
            <ActionMenuButton onClick={toggleActionMenuOpen} ref={triggerRef} />
            {isActionMenuOpen && (
                <ActionMenu
                    popupClassName="modal-popup"
                    onClose={closeActionMenu}
                    ref={popupRef}
                    buttonRef={triggerRef}
                >
                    {fileType.match('Picture') && (
                        <ActionMenuItem
                            title="View Full Size"
                            hoverIcon={<FullScreenIcon />}
                            icon={<FullScreenIcon color={secondaryText} />}
                            onSelect={() => onOpenFullSizeImage(rowData)}
                        />
                    )}
                    {hasGroupActions && (
                        <ActionMenuItem
                            title="Make Primary Image"
                            hoverIcon={
                                <FontAwesomeIcon
                                    icon={solidFaStar}
                                    color="var(--favorite)"
                                />
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={isPrimary ? solidFaStar : faStar}
                                    color="var(--favorite)"
                                />
                            }
                            disabled={isPrimary}
                            onSelect={() => makePrimaryImage(rowData)}
                        />
                    )}
                    {canEditFileLibrary && fileType === 'Folder' && (
                        <ActionMenuItem
                            title="Rename Folder"
                            hoverIcon={<FontAwesomeIcon icon={solidFaFolder} />}
                            icon={
                                <FontAwesomeIcon icon={faFolder} color={secondaryText} />
                            }
                            onSelect={() => {
                                setActiveAction('edit-folder');
                                closeActionMenu();
                            }}
                        />
                    )}
                    {canEditFileLibrary && fileType !== 'Invalid' && (
                        <ActionMenuItem
                            title={fileType === 'Folder' ? 'Move Folder' : 'Move File'}
                            hoverIcon={<FontAwesomeIcon icon={solidFaArrowAltFromLeft} />}
                            icon={
                                <FontAwesomeIcon
                                    icon={faArrowAltFromLeft}
                                    color={secondaryText}
                                />
                            }
                            onSelect={() => {
                                setActiveAction('move');
                                closeActionMenu();
                            }}
                        />
                    )}
                    {canDeleteFromFileLibrary &&
                        (enableModal ? (
                            <ActionMenuItem
                                title="Remove Association"
                                hoverIcon={<RemoveIcon />}
                                icon={<RemoveIcon color={secondaryText} />}
                                onSelect={() => {
                                    setActiveAction('unlink-group');
                                    closeActionMenu();
                                }}
                            />
                        ) : (
                            <ActionMenuItem
                                title="Delete"
                                hoverIcon={<DeleteIcon />}
                                icon={<DeleteIcon color={secondaryText} />}
                                onSelect={() => {
                                    setActiveAction('delete');
                                    closeActionMenu();
                                }}
                            />
                        ))}
                </ActionMenu>
            )}
            {activeAction === 'delete' && (
                <ConfirmModal
                    title={`Delete '${rowData.DisplayName}'`}
                    actionText={fileDeleteText.actionText}
                    checkText={fileDeleteText.checkText}
                    actionButtonText="Delete"
                    onAction={() => onDeleteConfirm()}
                    onClose={() => setActiveAction(undefined)}
                />
            )}
            {activeAction === 'unlink-group' && group && (
                <ConfirmModal
                    warningText="The file will still be available in the File Library."
                    title={rowData.DisplayName}
                    actionText={`remove ${group.Name}'s association to this file?`}
                    checkText="remove this file association"
                    actionButtonText="Remove"
                    onAction={() => onRemoveAssociationConfirm()}
                    onClose={() => setActiveAction(undefined)}
                />
            )}
            {activeAction === 'edit-folder' &&
                rowData instanceof PictureFileMetadataResponse && (
                    <FolderModal
                        folder={rowData}
                        onSave={() => {
                            onChange();
                            setActiveAction(undefined);
                        }}
                        onCancel={() => setActiveAction(undefined)}
                    />
                )}
            {activeAction === 'move' && (
                <DirentMoveModal
                    fileToBeMoved={rowData}
                    onSave={() => {
                        onChange();
                        setActiveAction(undefined);
                    }}
                    onCancel={() => setActiveAction(undefined)}
                    locationId={locationId}
                    corporationId={corporationId}
                    foldersHierarchy={foldersHierarchy}
                />
            )}
        </ActionMenuTableCell>
    );
}
